import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Send, Hash, MessageSquare, Users, ArrowLeft, Search, Circle, PhoneCall, Loader2
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { it } from 'date-fns/locale';

const TAB_CHANNELS = 'channels';
const TAB_DM = 'dm';

const formatMsgTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isToday(d)) return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Ieri ' + format(d, 'HH:mm');
  return format(d, 'dd/MM HH:mm', { locale: it });
};

const groupByDate = (msgs, tsField = 'timestamp') => {
  const g = {};
  msgs.forEach(m => {
    const d = new Date(m[tsField]);
    let k = isToday(d) ? 'Oggi' : isYesterday(d) ? 'Ieri' : format(d, 'dd MMMM yyyy', { locale: it });
    if (!g[k]) g[k] = [];
    g[k].push(m);
  });
  return g;
};

export const Chat = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(TAB_CHANNELS);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelMessages, setChannelMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [dmConversations, setDmConversations] = useState([]);
  const [selectedDmUser, setSelectedDmUser] = useState(null);
  const [dmMessages, setDmMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [callingUserId, setCallingUserId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchChannels();
    fetchUsers();
    fetchDmConversations();
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      fetchChannelMessages(selectedChannel.id);
      const iv = setInterval(() => fetchChannelMessages(selectedChannel.id), 4000);
      return () => clearInterval(iv);
    }
  }, [selectedChannel?.id]);

  useEffect(() => {
    if (selectedDmUser) {
      fetchDmMessages(selectedDmUser.id);
      const iv = setInterval(() => fetchDmMessages(selectedDmUser.id), 3000);
      return () => clearInterval(iv);
    }
  }, [selectedDmUser?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [channelMessages, dmMessages]);

  // Fetch DM conversations periodically
  useEffect(() => {
    const iv = setInterval(fetchDmConversations, 8000);
    return () => clearInterval(iv);
  }, []);

  const fetchChannels = async () => {
    try {
      const res = await api.get('/chat/channels');
      setChannels(res.data.channels);
    } catch {}
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data.filter(u => u.id !== user?.id));
    } catch {}
  };

  const fetchDmConversations = async () => {
    try {
      const res = await api.get('/chat/dm/conversations');
      setDmConversations(res.data);
    } catch {}
  };

  const fetchChannelMessages = async (channelId) => {
    try {
      const res = await api.get(`/chat/messages/${channelId}`);
      setChannelMessages(res.data);
    } catch {}
  };

  const fetchDmMessages = async (userId) => {
    try {
      const res = await api.get(`/chat/dm/${userId}/messages`);
      setDmMessages(res.data);
    } catch {}
  };

  const handleSendChannel = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChannel) return;
    try {
      await api.post('/chat/messages', { canale: selectedChannel.id, testo: newMessage });
      setNewMessage('');
      fetchChannelMessages(selectedChannel.id);
    } catch { toast.error('Errore invio'); }
  };

  const handleSendDm = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedDmUser) return;
    try {
      await api.post(`/chat/dm/${selectedDmUser.id}/messages`, { testo: newMessage });
      setNewMessage('');
      fetchDmMessages(selectedDmUser.id);
      fetchDmConversations();
    } catch { toast.error('Errore invio'); }
  };

  const selectDmUser = (u) => {
    setSelectedDmUser(u);
    setSelectedChannel(null);
    setNewMessage('');
  };

  const selectChannel = (ch) => {
    setSelectedChannel(ch);
    setSelectedDmUser(null);
    setNewMessage('');
  };

  const handleCallInterno = async (targetUser) => {
    const ext = targetUser.interno_voip;
    if (!ext) { toast.error(`${targetUser.nome} non ha un interno VoIP assegnato`); return; }
    setCallingUserId(targetUser.id);
    try {
      const res = await api.post('/centralino/call', { number: ext, description: `Interno ${targetUser.nome} ${targetUser.cognome || ''}` });
      toast.success(res.data.message || `Chiamata a ${targetUser.nome} in corso...`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore avvio chiamata'); }
    finally { setCallingUserId(null); }
  };

  const activeView = selectedChannel ? 'channel' : selectedDmUser ? 'dm' : null;

  // Filter users for DM tab
  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return `${u.nome} ${u.cognome}`.toLowerCase().includes(q);
  });

  // Get DM user list: merge conversations with all users
  const dmUserList = (() => {
    const convMap = {};
    dmConversations.forEach(c => { convMap[c.other_user.id] = c; });
    const list = [];
    // Conversations first
    dmConversations.forEach(c => {
      if (!searchQuery || `${c.other_user.nome} ${c.other_user.cognome}`.toLowerCase().includes(searchQuery.toLowerCase())) {
        list.push({ user: c.other_user, lastMsg: c.last_message, lastTs: c.last_timestamp, unread: c.unread_count });
      }
    });
    // Then remaining users
    filteredUsers.forEach(u => {
      if (!convMap[u.id]) {
        list.push({ user: { id: u.id, nome: u.nome, cognome: u.cognome, avatar: u.avatar, is_online: u.is_online, interno_voip: u.interno_voip }, lastMsg: null, lastTs: null, unread: 0 });
      }
    });
    return list;
  })();

  return (
    <div className="flex h-[calc(100dvh-48px)] bg-slate-100" data-testid="chat-page">
      {/* Sidebar */}
      <div className={`w-full sm:w-[320px] bg-white border-r border-slate-200 flex flex-col flex-shrink-0 ${activeView ? 'hidden sm:flex' : 'flex'}`}>
        {/* Tabs */}
        <div className="px-4 pt-4 pb-2 border-b border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-bold text-slate-900">Chat Interna</h1>
          </div>
          <div className="flex bg-slate-100 rounded-lg p-0.5 mb-3">
            <button
              onClick={() => setTab(TAB_CHANNELS)}
              data-testid="tab-channels"
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                tab === TAB_CHANNELS ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Hash size={14} /> Canali
            </button>
            <button
              onClick={() => setTab(TAB_DM)}
              data-testid="tab-dm"
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                tab === TAB_DM ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Users size={14} /> Messaggi
              {dmConversations.reduce((s, c) => s + (c.unread_count || 0), 0) > 0 && (
                <Badge className="bg-blue-500 text-white text-[9px] h-4 px-1.5 ml-1">
                  {dmConversations.reduce((s, c) => s + (c.unread_count || 0), 0)}
                </Badge>
              )}
            </button>
          </div>
          {tab === TAB_DM && (
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cerca utente..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-8 text-xs bg-slate-50" data-testid="chat-search" />
            </div>
          )}
        </div>

        {/* List */}
        <ScrollArea className="flex-1">
          {tab === TAB_CHANNELS ? (
            <div className="p-2 space-y-0.5">
              {channels.map(ch => (
                <button
                  key={ch.id}
                  onClick={() => selectChannel(ch)}
                  data-testid={`channel-${ch.id}`}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 transition-all ${
                    selectedChannel?.id === ch.id ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${
                    selectedChannel?.id === ch.id ? 'bg-blue-100' : 'bg-slate-100'
                  }`}>
                    <Hash size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{ch.nome}</p>
                    <p className="text-[11px] text-slate-400 truncate">{ch.descrizione}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-2 space-y-0.5">
              {dmUserList.map(item => (
                <button
                  key={item.user.id}
                  onClick={() => selectDmUser(item.user)}
                  data-testid={`dm-user-${item.user.id}`}
                  className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 transition-all ${
                    selectedDmUser?.id === item.user.id ? 'bg-blue-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-slate-100 text-slate-600 text-xs font-semibold">
                        {item.user.nome?.[0]}{item.user.cognome?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    {item.user.is_online && (
                      <div className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 border-2 border-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm truncate ${item.unread > 0 ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                        {item.user.nome} {item.user.cognome}
                      </span>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        {item.user.interno_voip && (
                          <button onClick={(e) => { e.stopPropagation(); handleCallInterno(item.user); }}
                            disabled={callingUserId === item.user.id}
                            className="p-1 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50"
                            title={`Chiama interno ${item.user.interno_voip}`}
                            data-testid={`call-user-${item.user.id}`}>
                            {callingUserId === item.user.id ? <Loader2 size={11} className="animate-spin" /> : <PhoneCall size={11} />}
                          </button>
                        )}
                        {item.lastTs && (
                          <span className="text-[10px] text-slate-400">{formatMsgTime(item.lastTs)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className={`text-xs truncate ${item.unread > 0 ? 'text-slate-700' : 'text-slate-400'}`}>
                        {item.lastMsg || (item.user.is_online ? 'Online' : 'Offline')}
                      </p>
                      {item.unread > 0 && (
                        <Badge className="bg-blue-500 text-white text-[9px] h-4 px-1.5 ml-1 flex-shrink-0">{item.unread}</Badge>
                      )}
                    </div>
                  </div>
                </button>
              ))}
              {dmUserList.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Users size={24} className="mx-auto mb-2" />
                  <p className="text-xs">Nessun utente trovato</p>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col min-w-0 ${!activeView ? 'hidden sm:flex' : 'flex'}`}>
        {activeView === 'channel' && selectedChannel && (
          <>
            <div className="h-[60px] px-5 flex items-center gap-3 bg-white border-b border-slate-200 flex-shrink-0">
              <Button variant="ghost" size="icon" className="sm:hidden h-8 w-8" onClick={() => setSelectedChannel(null)} data-testid="chat-back">
                <ArrowLeft size={18} />
              </Button>
              <div className="h-9 w-9 rounded-lg bg-blue-100 flex items-center justify-center">
                <Hash size={16} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900">{selectedChannel.nome}</h2>
                <p className="text-[11px] text-slate-500">{selectedChannel.descrizione}</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 bg-slate-50" data-testid="channel-messages">
              {Object.entries(groupByDate(channelMessages)).map(([date, msgs]) => (
                <div key={date}>
                  <div className="flex justify-center my-3">
                    <span className="bg-white px-3 py-1 rounded-lg text-[11px] text-slate-500 font-medium shadow-sm border border-slate-100">{date}</span>
                  </div>
                  {msgs.map((msg, i) => {
                    const isOwn = msg.user_id === user?.id;
                    return (
                      <div key={msg.id || i} className={`flex gap-2.5 mb-3 ${isOwn ? 'flex-row-reverse' : ''}`} data-testid={`msg-${msg.id}`}>
                        <Avatar className="h-8 w-8 flex-shrink-0 mt-0.5">
                          <AvatarFallback className="bg-slate-100 text-slate-600 text-[10px] font-semibold">
                            {msg.user_nome?.split(' ').map(w => w[0]).join('').slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div className={`max-w-[65%] ${isOwn ? 'text-right' : ''}`}>
                          <div className={`flex items-center gap-2 mb-0.5 ${isOwn ? 'flex-row-reverse' : ''}`}>
                            <span className="text-[11px] font-semibold text-slate-600">{msg.user_nome}</span>
                            <span className="text-[10px] text-slate-400">{formatMsgTime(msg.timestamp)}</span>
                          </div>
                          <div className={`inline-block px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                            isOwn ? 'bg-blue-600 text-white rounded-br-md' : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md shadow-sm'
                          }`}>
                            {msg.testo}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              {channelMessages.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Hash size={32} className="text-slate-300 mb-3" />
                  <p className="text-sm text-slate-500">Nessun messaggio in #{selectedChannel.nome}</p>
                  <p className="text-xs text-slate-400 mt-1">Inizia la conversazione!</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="bg-white border-t border-slate-200 px-5 py-3 flex-shrink-0">
              <form onSubmit={handleSendChannel} className="flex items-center gap-2">
                <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={`Scrivi in #${selectedChannel.nome}...`} className="flex-1 h-10 bg-slate-50 text-sm" data-testid="channel-msg-input" />
                <Button type="submit" size="icon" className="h-10 w-10 rounded-full bg-blue-600 hover:bg-blue-700" disabled={!newMessage.trim()} data-testid="channel-send-btn">
                  <Send size={18} />
                </Button>
              </form>
            </div>
          </>
        )}

        {activeView === 'dm' && selectedDmUser && (
          <>
            <div className="h-[60px] px-5 flex items-center gap-3 bg-white border-b border-slate-200 flex-shrink-0">
              <Button variant="ghost" size="icon" className="sm:hidden h-8 w-8" onClick={() => setSelectedDmUser(null)} data-testid="dm-back">
                <ArrowLeft size={18} />
              </Button>
              <div className="relative">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-slate-100 text-slate-600 text-xs font-semibold">
                    {selectedDmUser.nome?.[0]}{selectedDmUser.cognome?.[0]}
                  </AvatarFallback>
                </Avatar>
                {selectedDmUser.is_online && (
                  <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 border-2 border-white" />
                )}
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900">{selectedDmUser.nome} {selectedDmUser.cognome}</h2>
                <p className="text-[11px] text-slate-500">{selectedDmUser.is_online ? 'Online' : 'Offline'}</p>
              </div>
              <div className="ml-auto">
                {selectedDmUser.interno_voip && (
                  <Button variant="outline" size="sm" className="h-8 text-xs"
                    onClick={() => handleCallInterno(selectedDmUser)}
                    disabled={callingUserId === selectedDmUser.id}
                    data-testid="call-interno-btn">
                    {callingUserId === selectedDmUser.id ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <PhoneCall size={14} className="mr-1.5" />}
                    Chiama Int. {selectedDmUser.interno_voip}
                  </Button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 bg-slate-50" data-testid="dm-messages">
              {Object.entries(groupByDate(dmMessages)).map(([date, msgs]) => (
                <div key={date}>
                  <div className="flex justify-center my-3">
                    <span className="bg-white px-3 py-1 rounded-lg text-[11px] text-slate-500 font-medium shadow-sm border border-slate-100">{date}</span>
                  </div>
                  {msgs.map((msg, i) => {
                    const isOwn = msg.sender_id === user?.id;
                    return (
                      <div key={msg.id || i} className={`flex mb-2 ${isOwn ? 'justify-end' : 'justify-start'}`} data-testid={`dm-msg-${msg.id}`}>
                        <div className={`max-w-[70%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                          isOwn ? 'bg-blue-600 text-white rounded-br-md' : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md shadow-sm'
                        }`}>
                          <p className="whitespace-pre-wrap">{msg.testo}</p>
                          <p className={`text-[10px] mt-0.5 text-right ${isOwn ? 'text-blue-200' : 'text-slate-400'}`}>
                            {formatMsgTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              {dmMessages.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <MessageSquare size={32} className="text-slate-300 mb-3" />
                  <p className="text-sm text-slate-500">Nessun messaggio con {selectedDmUser.nome}</p>
                  <p className="text-xs text-slate-400 mt-1">Scrivi il primo messaggio!</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="bg-white border-t border-slate-200 px-5 py-3 flex-shrink-0">
              <form onSubmit={handleSendDm} className="flex items-center gap-2">
                <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={`Scrivi a ${selectedDmUser.nome}...`} className="flex-1 h-10 bg-slate-50 text-sm" data-testid="dm-msg-input" />
                <Button type="submit" size="icon" className="h-10 w-10 rounded-full bg-blue-600 hover:bg-blue-700" disabled={!newMessage.trim()} data-testid="dm-send-btn">
                  <Send size={18} />
                </Button>
              </form>
            </div>
          </>
        )}

        {!activeView && (
          <div className="hidden sm:flex flex-1 items-center justify-center bg-slate-50" data-testid="chat-no-selection">
            <div className="text-center max-w-sm">
              <div className="h-20 w-20 rounded-3xl bg-white shadow-sm flex items-center justify-center mx-auto mb-5 border border-slate-100">
                <MessageSquare size={36} className="text-blue-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">Chat Interna</h2>
              <p className="text-sm text-slate-500">Seleziona un canale o un collega per iniziare una conversazione</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
